import type { ChannelSummary, CommsMember, CommsMessage } from '../../interfaces/ICommsApi.js';

/** A member's display name: the full name when Trusstor has one, else the username. */
export function memberName(member: CommsMember): string {
  return member.name ?? member.username;
}

/**
 * An author's current display name from the members directory. Falls back to
 * the name saved with the message when the author isn't on it (e.g. they left
 * the project) or the saved preview predates `authorId`.
 */
export function authorName(authorId: string | undefined, savedName: string, members: CommsMember[]): string {
  const member = members.find((m) => m.id === authorId);
  return member ? memberName(member) : savedName;
}

/** Messages with each author's name resolved against the members directory. */
export function withAuthorNames<T extends CommsMessage>(messages: T[], members: CommsMember[]): T[] {
  return messages.map((message) => ({
    ...message,
    authorName: authorName(message.authorId, message.authorName, members),
  }));
}

/** The channel with its last-message preview's author name resolved against the members directory. */
export function withPreviewAuthorName<T extends ChannelSummary>(channel: T, members: CommsMember[]): T {
  const preview = channel.lastMessagePreview;
  if (!preview) return channel;
  return { ...channel, lastMessagePreview: { ...preview, authorName: authorName(preview.authorId, preview.authorName, members) } };
}
